import { Fragment, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  useCreateDomain,
  useDeleteDomain,
  useDnsHealth,
  useDomains,
} from "@/api/hooks";
import {
  ApiError,
  type DnsCheck,
  type DnsCheckStatus,
  type Domain,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

const schema = z.object({
  name: z
    .string()
    .min(3)
    .max(253)
    .regex(
      /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i,
      "Enter a valid domain (e.g. example.com)",
    )
    .transform((s) => s.toLowerCase()),
});

export default function DomainsPage() {
  const { data, isLoading } = useDomains();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Domain | null>(null);
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">D</span>omains
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Virtual domains served by Postfix. Adding a domain here lets the mail
            server accept mail for it immediately; MX, SPF, DMARC and DKIM records
            still need to be set up in DNS separately.
          </p>
        </div>
        <CreateDomainDialog />
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mailboxes</TableHead>
              <TableHead>Aliases</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="w-28 text-right">DNS</TableHead>
              <TableHead className="w-12 text-right" aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data?.domains.map((d) => {
              const isOpen = expanded === d.name;
              return (
                <Fragment key={d.name}>
                  <TableRow>
                    <TableCell className="font-mono">{d.name}</TableCell>
                    <TableCell>
                      <Badge variant={d.active ? "secondary" : "outline"}>
                        {d.active ? "active" : "disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell>{d.mailbox_count}</TableCell>
                    <TableCell>{d.alias_count}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(d.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpanded(isOpen ? null : d.name)}
                      >
                        {isOpen ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                        Check
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setToDelete(d)}
                        title="Delete domain"
                        aria-label={`Delete ${d.name}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/30 p-4">
                        <DnsRecordsPanel domain={d.name} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {data && data.domains.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No domains yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteDomainDialog
        domain={toDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}

function DeleteDomainDialog({
  domain,
  onClose,
}: {
  domain: Domain | null;
  onClose: () => void;
}) {
  const del = useDeleteDomain();
  const [error, setError] = useState<
    | { kind: "dependents"; mailboxes: number; aliases: number }
    | { kind: "other"; message: string }
    | null
  >(null);
  const hasBlockers =
    !!domain &&
    (Number(domain.mailbox_count) > 0 || Number(domain.alias_count) > 0);
  return (
    <Dialog
      open={!!domain}
      onOpenChange={(v) => {
        if (!v) {
          setError(null);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete domain</DialogTitle>
          <DialogDescription>
            This removes{" "}
            <code className="font-mono">{domain?.name}</code> from the mail server
            and deletes any DKIM keys attached to it. Published DNS records are not
            touched — remove them at the registrar separately.
          </DialogDescription>
        </DialogHeader>
        {hasBlockers && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            This domain still has{" "}
            <strong>{domain!.mailbox_count} mailbox(es)</strong> and{" "}
            <strong>{domain!.alias_count} alias(es)</strong>. Delete those first.
          </div>
        )}
        {error?.kind === "dependents" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            Refused by server: {error.mailboxes} mailbox(es), {error.aliases}{" "}
            alias(es) still reference this domain.
          </div>
        )}
        {error?.kind === "other" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            {error.message}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!domain || hasBlockers || del.isPending}
            onClick={async () => {
              if (!domain) return;
              setError(null);
              try {
                await del.mutateAsync(domain.name);
                onClose();
              } catch (err) {
                if (err instanceof ApiError) {
                  if (err.code === "has_dependents") {
                    const d = err.details as
                      | { mailboxes?: number; aliases?: number }
                      | undefined;
                    setError({
                      kind: "dependents",
                      mailboxes: d?.mailboxes ?? 0,
                      aliases: d?.aliases ?? 0,
                    });
                    return;
                  }
                  setError({ kind: "other", message: err.code });
                  return;
                }
                setError({ kind: "other", message: "failed" });
              }
            }}
          >
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDomainDialog() {
  const [open, setOpen] = useState(false);
  const create = useCreateDomain();
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          New domain
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add domain</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (v) => {
            try {
              await create.mutateAsync(v);
              form.reset();
              setOpen(false);
            } catch (err) {
              form.setError("name", {
                message: err instanceof ApiError ? err.code : "failed",
              });
            }
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="name">Domain</Label>
            <Input id="name" placeholder="example.com" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <DnsRecordsHelp typed={form.watch("name")} />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DnsRecordsHelp({ typed }: { typed?: string }) {
  const isValid =
    !!typed && /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(typed);
  const domain = isValid ? typed!.toLowerCase() : null;
  return (
    <details className="rounded-md border border-border bg-muted/30 text-xs" open>
      <summary className="cursor-pointer select-none px-3 py-2 font-medium text-foreground">
        DNS records to publish for this domain
      </summary>
      <div className="px-3 pb-3">
        <DnsRecordsPanel
          domain={domain}
          intro={
            <p className="text-muted-foreground">
              Publish these at the registrar, then click <em>Check</em> to verify each
              record resolves. You can click <em>Add</em> at any time — the domain will
              start accepting mail even if DNS isn't fully set yet.
            </p>
          }
        />
      </div>
    </details>
  );
}

function DnsRecordsPanel({
  domain,
  intro,
}: {
  domain: string | null;
  intro?: ReactNode;
}) {
  const [run, setRun] = useState(false);
  const { data, isFetching, refetch, dataUpdatedAt } = useDnsHealth(
    run && domain ? domain : null,
  );
  const checks = data?.checks ?? [];
  const byKey = new Map(checks.map((c) => [c.key, c]));
  const dkim = checks.filter((c) => c.key.startsWith("dkim:") && c.key !== "dkim:none");
  const ptr = byKey.get("ptr");
  const dkimNone = byKey.get("dkim:none");
  const displayDomain = domain ?? "<domain>";
  const showChecks = run && !!domain;

  return (
    <div className="space-y-3 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {intro ?? (
            <p className="text-muted-foreground">
              Publish these at the registrar for{" "}
              <code className="font-mono">{displayDomain}</code>. Click <em>Check</em> to
              resolve each record from public DNS.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showChecks && dataUpdatedAt > 0 && !isFetching && (
            <span className="text-muted-foreground">
              {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (!domain) return;
              if (!run) setRun(true);
              else refetch();
            }}
            disabled={!domain || isFetching}
            title={!domain ? "Enter a valid domain first" : undefined}
          >
            <ArrowPathIcon className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
            {isFetching ? "Checking…" : run ? "Re-check" : "Check"}
          </Button>
        </div>
      </div>
      <DnsRecordsTable
        domain={displayDomain}
        byKey={showChecks ? byKey : undefined}
        dkimRows={showChecks ? dkim : undefined}
        dkimNone={showChecks ? dkimNone : undefined}
      />
      {showChecks && ptr && (
        <div className="flex items-start gap-2 rounded-md border border-border/60 bg-background/40 px-3 py-2">
          <StatusBadge check={ptr} />
          <div className="space-y-0.5">
            <div className="font-medium">{ptr.label}</div>
            {ptr.actual && (
              <div className="font-mono text-muted-foreground break-all">
                actual: {ptr.actual}
              </div>
            )}
            {ptr.note && <div className="text-muted-foreground">{ptr.note}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

type RecordRow = {
  type: string;
  name: string;
  value: string;
  checkKey?: string;
};

function DnsRecordsTable({
  domain,
  byKey,
  dkimRows,
  dkimNone,
}: {
  domain: string;
  byKey?: Map<string, DnsCheck>;
  dkimRows?: DnsCheck[];
  dkimNone?: DnsCheck;
}) {
  const rows: RecordRow[] = [
    { type: "A", name: `mail.${domain}`, value: "<Public IP of this email server>", checkKey: "a" },
    { type: "MX", name: `${domain}.`, value: `mail.${domain}  (priority 10)`, checkKey: "mx" },
    { type: "TXT", name: `${domain}.`, value: "v=spf1 mx ~all", checkKey: "spf" },
    {
      type: "TXT",
      name: `_dmarc.${domain}`,
      value: `v=DMARC1; p=none; rua=mailto:postmaster@${domain}`,
      checkKey: "dmarc",
    },
  ];
  if (dkimRows && dkimRows.length > 0) {
    for (const c of dkimRows) {
      const selector = c.key.slice("dkim:".length);
      rows.push({
        type: "TXT",
        name: `${selector}._domainkey.${domain}`,
        value: c.actual ?? "v=DKIM1; k=rsa; p=…",
        checkKey: c.key,
      });
    }
  } else {
    rows.push({
      type: "TXT",
      name: `<selector>._domainkey.${domain}`,
      value: "generated on the DKIM page",
    });
  }
  const showStatus = !!byKey;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse font-mono text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-3 font-normal">Type</th>
            <th className="py-1 pr-3 font-normal">Name</th>
            <th className="py-1 pr-3 font-normal">Value</th>
            {showStatus && <th className="py-1 font-normal">Status</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const check = r.checkKey ? byKey?.get(r.checkKey) : undefined;
            return (
              <tr
                key={`${r.type}-${r.name}`}
                className="border-t border-border/60 align-top"
              >
                <td className="py-1 pr-3">{r.type}</td>
                <td className="py-1 pr-3">
                  <CopyCell text={r.name} />
                </td>
                <td className="py-1 pr-3">
                  <CopyCell text={r.value} />
                </td>
                {showStatus && (
                  <td className="py-1">
                    <StatusCell check={check} placeholder={/[<>]/.test(r.name)} />
                  </td>
                )}
              </tr>
            );
          })}
          {showStatus && dkimNone && (
            <tr className="border-t border-border/60 align-top">
              <td className="py-1 pr-3" colSpan={3}>
                <span className="text-muted-foreground font-sans">
                  No DKIM selectors configured — generate one on the DKIM page.
                </span>
              </td>
              <td className="py-1">
                <StatusBadge check={dkimNone} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatusCell({
  check,
  placeholder,
}: {
  check?: DnsCheck;
  placeholder?: boolean;
}) {
  if (placeholder)
    return <span className="text-muted-foreground font-sans">—</span>;
  if (!check)
    return <span className="text-muted-foreground font-sans">—</span>;
  return (
    <div className="space-y-1 font-sans">
      <StatusBadge check={check} />
      {check.status !== "pass" && check.actual && (
        <div className="text-muted-foreground break-all">
          got: <span className="font-mono">{check.actual}</span>
        </div>
      )}
      {check.note && (
        <div className="text-muted-foreground">{check.note}</div>
      )}
    </div>
  );
}

const STATUS_STYLES: Record<DnsCheckStatus, string> = {
  pass: "bg-green-500/15 text-green-500 ring-green-500/30",
  warn: "bg-amber-500/15 text-amber-500 ring-amber-500/30",
  fail: "bg-red-500/15 text-red-500 ring-red-500/30",
  info: "bg-slate-500/15 text-slate-400 ring-slate-500/30",
};

function StatusBadge({ check }: { check: DnsCheck }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset font-sans",
        STATUS_STYLES[check.status],
      )}
      title={check.note ?? undefined}
    >
      {check.status}
    </span>
  );
}

function CopyCell({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const isPlaceholder = /[<>]/.test(text);
  return (
    <div className="flex items-start gap-1.5">
      <span className="flex-1 break-all">{text}</span>
      {!isPlaceholder && (
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
          title={copied ? "Copied" : "Copy"}
          aria-label={copied ? "Copied" : "Copy"}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {copied ? (
            <CheckIcon className="h-3.5 w-3.5" />
          ) : (
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </div>
  );
}
